// React imports and libraries
import { useEffect, useState, useMemo } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import api from '../../config/api';

// Utility data
import { leanOptions } from '../../formData/styleData';

// Images and icons
import leftLeanToIcon from '../../assets/left-lean-to.svg';
import centerSectionIcon from '../../assets/center-section.svg';
import backLeanToIcon from '../../assets/back-lean-to.svg';
import frontLeanToIcon from '../../assets/front-lean-to.svg';
import rightLeanToIcon from '../../assets/right-lean-to.svg';

/**
 * SideEnds
 *
 * Handles configuration and pricing for:
 *  - Sidewalls (left/right)
 *  - Gable ends (front/back)
 *  - Lean-to sides around the main building
 *
 * Responsibilities:
 *  - Manage form state for panel selections (react-hook-form)
 *  - Derive pricing for sidewalls and gable ends via /Materials API
 *  - Notify parent via `onDataChange` and `setSidewallCosts`
 */
const SideEnds = ({
	formAdditionalOptionsData,
	setLeftLean,
	leftLean,
	setRightLean,
	rightLean,
	setFrontLean,
	setBackLean,
	gableEndsOptions,
	sidewallOptions,
	windBracingOptions,
	jTrimOptions,
	leanRoofPitch,
	onDataChange,
	selectedStyle,
	// Dimensions for each section (center + up to 4 lean-tos)
	cSWidth,
	cSLength,
	cSHeight,
	bLWidth,
	bLLength,
	bLHeight,
	lLWidth,
	lLLength,
	lLHeight,
	fLWidth,
	fLLength,
	fLHeight,
	rLWidth,
	rLLength,
	rLHeight,
	setSidewallCosts,
	sideEndData,
}) => {
	// JWT token used for API calls
	const token = localStorage.getItem('token');



	/**
	 * Filter sidewall options based on the current leg height.
	 * - Always allow "fullyEnclosed" and "0" (open)
	 * - For numeric options, only allow panel heights < building height
	 */
	const filterSidewallOptions = (currentHeight) => {
		// If there is no height yet, return the full list
		if (!currentHeight) {
			return sidewallOptions;
		}

		return sidewallOptions.filter((option) => {
			// These options are not height-based, so always include them
			if (option.value === 'fullyEnclosed' || option.value === '0') {
				return true;
			}

			// Height-based side panel; must be shorter than the leg height
			const panelHeight = parseInt(option.value, 10);
			return panelHeight < currentHeight;
		});
	};





	/**
	 * Dimension map for each section key used by the form.
	 * This allows cost calculators to look up width/length/height by section name.
	 */
	const dims = useMemo(
		() => ({
			centerSectionSide: { width: cSWidth, length: cSLength, height: cSHeight },
			backLeanToSide: { width: bLWidth, length: bLLength, height: bLHeight },
			leftLeanToSide: { width: lLWidth, length: lLLength, height: lLHeight },
			frontLeanToSide: { width: fLWidth, length: fLLength, height: fLHeight },
			rightLeanToSide: { width: rLWidth, length: rLLength, height: rLHeight },
		}),
		[
			cSWidth,
			cSLength,
			cSHeight,
			bLWidth,
			bLLength,
			bLHeight,
			lLWidth,
			lLLength,
			lLHeight,
			fLWidth,
			fLLength,
			fLHeight,
			rLWidth,
			rLLength,
			rLHeight,
		]
	);

	/**
	 * React Hook Form setup.
	 * Initializes default values for all sections:
	 *  - centerSectionSide
	 *  - backLeanToSide
	 *  - leftLeanToSide
	 *  - frontLeanToSide
	 *  - rightLeanToSide
	 */
	const { control, reset } = useForm({
		defaultValues: {
			centerSectionSide: {
				frontGableEnd: gableEndsOptions[0]?.value,
				leftSidewall: sidewallOptions[0]?.value,
				rightSidewall: sidewallOptions[0]?.value,
				backGableEnd: gableEndsOptions[0]?.value,
				windBracing: windBracingOptions[0]?.value,
				jTrim: jTrimOptions[0]?.value,
			},
			backLeanToSide: {
				backLean: leanOptions[0]?.value,
				backLeanRoofPitch: leanRoofPitch[0]?.value,
				frontGableEnd: gableEndsOptions[0]?.value,
				leftSidewall: sidewallOptions[0]?.value,
				rightSidewall: sidewallOptions[0]?.value,
				backGableEnd: gableEndsOptions[0]?.value,
				windBracing: windBracingOptions[0]?.value,
				jTrim: jTrimOptions[0]?.value,
			},
			leftLeanToSide: {
				leftLean: leanOptions[0]?.value,
				leftLeanRoofPitch: leanRoofPitch[0]?.value,
				frontGableEnd: gableEndsOptions[0]?.value,
				leftSidewall: sidewallOptions[0]?.value,
				rightSidewall: sidewallOptions[0]?.value,
				backGableEnd: gableEndsOptions[0]?.value,
				windBracing: windBracingOptions[0]?.value,
				jTrim: jTrimOptions[0]?.value,
			},
			frontLeanToSide: {
				frontLean: leanOptions[0]?.value,
				frontLeanRoofPitch: leanRoofPitch[0]?.value,
				frontGableEnd: gableEndsOptions[0]?.value,
				leftSidewall: sidewallOptions[0]?.value,
				rightSidewall: sidewallOptions[0]?.value,
				backGableEnd: gableEndsOptions[0]?.value,
				windBracing: windBracingOptions[0]?.value,
				jTrim: jTrimOptions[0]?.value,
			},
			rightLeanToSide: {
				rightLean: leanOptions[0]?.value,
				rightLeanRoofPitch: leanRoofPitch[0]?.value,
				frontGableEnd: gableEndsOptions[0]?.value,
				leftSidewall: sidewallOptions[0]?.value,
				rightSidewall: sidewallOptions[0]?.value,
				backGableEnd: gableEndsOptions[0]?.value,
				windBracing: windBracingOptions[0]?.value,
				jTrim: jTrimOptions[0]?.value,
			},
		},
	});

	// Subscribe to all form values; `values` is the full configuration object
	const values = useWatch({ control });
	const sections = Object.keys(values);

	// UI state: which tab/section is currently active in the Side & Ends UI
	const [activeTabSide, setActiveTabSide] = useState('centerSectionSide');
	const handleTabClickSide = (name) => setActiveTabSide(name);

	/**
	 * Default options for left/right lean selection.
	 * Used mainly for barn styles that pre-populate leans.
	 */
	const [defaultOptions, setDefaultOptions] = useState({
		leftLean: '',
		rightLean: '',
	});

	// Keep defaultOptions in sync with parent-level leftLean / rightLean props
	useEffect(() => {
		setDefaultOptions((prev) => ({
			...prev,
			leftLean: leftLean || prev.leftLean,
			rightLean: rightLean || prev.rightLean,
		}));
	}, [leftLean, rightLean]);

	/**
	 * Helper to determine the category ID for a specific gable selection.
	 */
	const getGableCategory = (selection, isVertical) => {
		if (selection === 'extendedGableEnd') {
			return isVertical ? 21 : 45;
		}
		if (
			selection === 'gableEnd' ||
			selection === 'leftSideWithGableEnd' ||
			selection === 'rightSideWithGableEnd'
		) {
			return isVertical ? 50 : 44;
		}
		if (
			selection === 'fullyEnclosed' ||
			selection === 'leftSideEnclosed' ||
			selection === 'rightSideEnclosed'
		) {
			return isVertical ? 9 : 8;
		}
		// Fallback
		return isVertical ? 9 : 8;
	};

	/**
	 * Calculate the price of a sidewall panel for a given section and field
	 * (e.g. "centerSectionSide.leftSidewall").
	 *
	 * Uses:
	 *  - dims[section].length  -> structure length
	 *  - panelHeight           -> fully enclosed (full height) or partial height
	 *  - sidewall orientation  -> decides categoryId (6 vs 35)
	 */
	const calculateSideWallCost = async (section, fieldName) => {
		const opt = values[section][fieldName];
		const { length, height } = dims[section];
		const panelHeight = opt === 'fullyEnclosed' ? Number(height) : Number(opt);

		if (!length || !panelHeight) return 0;

		const sideWallType = formAdditionalOptionsData?.sidewall;
		if (!sideWallType) return 0;

		const isVertical = sideWallType === 'vertical';
		const sideCategory   = isVertical ? 6  : 35;
		const gableCategory  = isVertical ? 9  : 8;

		const roundUp5 = (val) => {
			const n = Number(val);
			return n % 5 === 0 ? n : Math.ceil(n / 5) * 5;
		};
		const wallRun = roundUp5(length);

		const fetch1 = async (catId, params) => {
			try {
				const res = await api.get('/Materials', {
					params: { pageSize: 1, page: 1, sortOrder: 'DESC', categoryId: catId, ...params },
					headers: { Authorization: `Bearer ${token}` },
				});
				return res.data.items?.[0]?.defaultPrice ?? 0;
			} catch { return 0; }
		};

		// 1. Sidewall category — width as wall run
		let price = await fetch1(sideCategory, { width: wallRun, height: panelHeight });
		// 2. Sidewall category — length as wall run (original convention)
		if (!price) price = await fetch1(sideCategory, { length: wallRun, height: panelHeight });
		// 3. Sidewall strip pricing via width
		if (!price) { const u = await fetch1(sideCategory, { width: wallRun, height: 1 }); price = u * panelHeight; }
		// 4. Sidewall strip pricing via length
		if (!price) { const u = await fetch1(sideCategory, { length: wallRun, height: 1 }); price = u * panelHeight; }
		// 5. Gable category (8/9) with wall-run width — same physical panel, reuse data
		if (!price) price = await fetch1(gableCategory, { width: wallRun, height: panelHeight });
		// 6. Gable strip pricing
		if (!price) { const u = await fetch1(gableCategory, { width: wallRun, height: 1 }); price = u * panelHeight; }

		return price;
	};

	/**
	 * Calculate the price of a gable end panel for a given section and field
	 * (e.g. "centerSectionSide.frontGableEnd").
	 *
	 * Notes:
	 *  - Only priced after the user touches that selector (touchedGables)
	 *  - "open" and empty selections return 0
	 *  - Some panel types are half price (e.g. left/right side enclosed)
	 */
	const calculateGableEndCost = async (section, fieldName) => {

		const { width, height } = dims[section];
		const panelType = values[section][fieldName];

        console.group(`calculateGableEndCost: ${section}.${fieldName}`);
        console.log('Inputs:', { width, height, panelType, gableEndSidingPanels: formAdditionalOptionsData?.gableEndSidingPanels });

		// No gable panel selected, explicitly open, or panel orientation not selected
		if (panelType === 'open' || !panelType || !formAdditionalOptionsData?.gableEndSidingPanels) {
            console.log('Returning 0: Invalid inputs or Open');
            console.groupEnd();
			return 0;
		}

		try {
            const categoryId = getGableCategory(
						panelType,
						formAdditionalOptionsData?.gableEndSidingPanels === 'vertical'
					);
            console.log('Category ID:', categoryId);

			// Enclosed panels require both Width and Height.
            // Gable/Extended panels are priced by Width only (sending leg height results in 0).
            const enclosedTypes = ['fullyEnclosed', 'leftSideEnclosed', 'rightSideEnclosed'];
            const isEnclosed = enclosedTypes.includes(panelType);

            const effectiveHeight = isEnclosed ? Number(height) : 0;

            console.log(`Fetching price with Width: ${width}, Height: ${effectiveHeight} (Enclosed: ${isEnclosed})`);

			const { data } = await api.get('/Materials', {
				params: {
					pageSize: 100,
					page: 1,
					sortOrder: 'DESC',
					categoryId,
					width: Number(width),
					height: effectiveHeight,
				},
				headers: { Authorization: `Bearer ${token}` },
			});

			let price = data.items[0]?.defaultPrice ?? 0;
            console.log('API Price:', price);

			// Panel types that represent "half" coverage and should be priced at 50%
			const halfPricePanelTypes = [
				'leftSideEnclosed',
				'rightSideEnclosed',
				'leftSideWithGableEnd',
				'rightSideWithGableEnd',
			];

			if (halfPricePanelTypes.includes(panelType)) {
				price /= 2;
                console.log('Halved Price:', price);
			}

            console.groupEnd();
			return price;
		} catch (err) {
			console.error('Error fetching gable end cost:', err);
            console.groupEnd();
			return 0;
		}
	};

	/**
	 * Recalculate all sidewall & gable-end costs whenever:
	 *  - Form values change
	 *  - Gable selections are touched
	 *  - Sidewall / gable panel orientation settings change
	 *
	 * The result is pushed up via:
	 *  - setSidewallCosts(newCosts)
	 *  - onDataChange({ configuration: values, costs: newCosts })
	 */
	useEffect(() => {
		if (!values || Object.keys(values).length === 0) return;

		const recalcAll = async () => {
			const newCosts = {};

			for (const sec of sections) {
				newCosts[sec] = {
					leftSidewall: await calculateSideWallCost(sec, 'leftSidewall'),
					rightSidewall: await calculateSideWallCost(sec, 'rightSidewall'),
					frontGableEnd: await calculateGableEndCost(sec, 'frontGableEnd'),
					backGableEnd: await calculateGableEndCost(sec, 'backGableEnd'),
				};
			}

			setSidewallCosts(newCosts);
			onDataChange({ configuration: values, costs: newCosts });
		};

		recalcAll();
	}, [
		values,
		values,
		formAdditionalOptionsData.sidewall,
		formAdditionalOptionsData.gableEndSidingPanels,
		sideEndData?.configuration?.centerSectionSide?.frontGableEnd,
		sideEndData?.configuration?.backLeanToSide?.frontGableEnd,
		sideEndData?.configuration?.backLeanToSide?.backGableEnd,
		sideEndData?.configuration?.centerSectionSide?.backGableEnd,
		sideEndData?.configuration?.leftLeanToSide?.frontGableEnd,
		sideEndData?.configuration?.leftLeanToSide?.backGableEnd,
		sideEndData?.configuration?.frontLeanToSide?.frontGableEnd,
		sideEndData?.configuration?.frontLeanToSide?.backGableEnd,
		sideEndData?.configuration?.rightLeanToSide?.frontGableEnd,
		sideEndData?.configuration?.rightLeanToSide?.backGableEnd,
	]);

	/**
	 * Reset all form fields to their default values whenever
	 * the option lists change (e.g. after data reload or config change).
	 */
	useEffect(() => {
		const ENCLOSED_STYLES = ['barn', 'slbarn', 'rv', 'garage', 'garageleanto', 'utility', 'tw', 'hexbarn'];
		const isEnclosed = ENCLOSED_STYLES.includes(selectedStyle);
		const isBarn = selectedStyle === 'barn' || selectedStyle === 'slbarn';
		const defaultSidewall = isEnclosed ? 'fullyEnclosed' : sidewallOptions[0]?.value;
		const defaultGableEnd = isEnclosed ? 'fullyEnclosed' : gableEndsOptions[0]?.value;
		const defaultLeftLean = isBarn ? 'leanToOnly' : leanOptions[0]?.value;
		const defaultRightLean = isBarn ? 'leanToOnly' : leanOptions[0]?.value;

		reset({
			centerSectionSide: {
				frontGableEnd: defaultGableEnd,
				leftSidewall: defaultSidewall,
				rightSidewall: defaultSidewall,
				backGableEnd: defaultGableEnd,
				windBracing: windBracingOptions[0]?.value,
				jTrim: jTrimOptions[0]?.value,
			},
			backLeanToSide: {
				backLean: leanOptions[0]?.value,
				backLeanRoofPitch: leanRoofPitch[0]?.value,
				frontGableEnd: defaultGableEnd,
				leftSidewall: defaultSidewall,
				rightSidewall: defaultSidewall,
				backGableEnd: defaultGableEnd,
				windBracing: windBracingOptions[0]?.value,
				jTrim: jTrimOptions[0]?.value,
			},
			leftLeanToSide: {
				leftLean: defaultLeftLean,
				leftLeanRoofPitch: leanRoofPitch[0]?.value,
				frontGableEnd: defaultGableEnd,
				leftSidewall: defaultSidewall,
				rightSidewall: defaultSidewall,
				backGableEnd: defaultGableEnd,
				windBracing: windBracingOptions[0]?.value,
				jTrim: jTrimOptions[0]?.value,
			},
			frontLeanToSide: {
				frontLean: leanOptions[0]?.value,
				frontLeanRoofPitch: leanRoofPitch[0]?.value,
				frontGableEnd: defaultGableEnd,
				leftSidewall: defaultSidewall,
				rightSidewall: defaultSidewall,
				backGableEnd: defaultGableEnd,
				windBracing: windBracingOptions[0]?.value,
				jTrim: jTrimOptions[0]?.value,
			},
			rightLeanToSide: {
				rightLean: defaultRightLean,
				rightLeanRoofPitch: leanRoofPitch[0]?.value,
				frontGableEnd: defaultGableEnd,
				leftSidewall: defaultSidewall,
				rightSidewall: defaultSidewall,
				backGableEnd: defaultGableEnd,
				windBracing: windBracingOptions[0]?.value,
				jTrim: jTrimOptions[0]?.value,
			},
		});
	}, [
		selectedStyle,
		gableEndsOptions,
		sidewallOptions,
		leanRoofPitch,
		jTrimOptions,
		windBracingOptions,
	]);
	return (
		<>
			<div className="new-order-form">
				<p className="instructions-p">
					<strong>Section</strong> Select the panel options for the sidewall and
					endwalls of your building.
				</p>
				<div className="doors-windows-section">
					<ul className="tab-buttons">
						{selectedStyle === 'leanto' || selectedStyle === 'hexbarn' ? (
							<li
								className={
									activeTabSide === 'centerSectionSide'
										? 'tab-button active'
										: 'tab-button'
								}
								onClick={(j) => handleTabClickSide('centerSectionSide', j)}
							>
								<img
									src={centerSectionIcon}
									alt="Icon"
									className="icon-dwtab"
								/>
								<strong>Center Section</strong>
							</li>
						) : (
							<>
								<li
									className={
										activeTabSide === 'leftLeanToSide'
											? 'tab-button active'
											: 'tab-button'
									}
									onClick={(j) => handleTabClickSide('leftLeanToSide', j)}
								>
									<img src={leftLeanToIcon} alt="Icon" className="icon-dwtab" />
									<strong>Left Lean-to</strong>
								</li>
								<li
									className={
										activeTabSide === 'frontLeanToSide'
											? 'tab-button active'
											: 'tab-button'
									}
									onClick={(j) => handleTabClickSide('frontLeanToSide', j)}
								>
									<img
										src={frontLeanToIcon}
										alt="Icon"
										className="icon-dwtab"
									/>
									<strong>Front Lean-to</strong>
								</li>
								<li
									className={
										activeTabSide === 'centerSectionSide'
											? 'tab-button active'
											: 'tab-button'
									}
									onClick={(j) => handleTabClickSide('centerSectionSide', j)}
								>
									<img
										src={centerSectionIcon}
										alt="Icon"
										className="icon-dwtab"
									/>
									<strong>Center Section</strong>
								</li>
								<li
									className={
										activeTabSide === 'backLeanToSide'
											? 'tab-button active'
											: 'tab-button'
									}
									onClick={(j) => handleTabClickSide('backLeanToSide', j)}
								>
									<img src={backLeanToIcon} alt="Icon" className="icon-dwtab" />
									<strong>Back Lean To</strong>
								</li>
								<li
									className={
										activeTabSide === 'rightLeanToSide'
											? 'tab-button active'
											: 'tab-button'
									}
									onClick={(j) => handleTabClickSide('rightLeanToSide', j)}
								>
									<img
										src={rightLeanToIcon}
										alt="Icon"
										className="icon-dwtab"
									/>
									<strong>Right Lean-to</strong>
								</li>
							</>
						)}
					</ul>

					{activeTabSide === 'frontLeanToSide' && (
						<>
							<div className="field-section-half">
								<div className="inp-col">
									<label htmlFor="frontLean">
										<strong>Front Lean</strong>
									</label>
									<Controller
										name="frontLeanToSide.frontLean"
										control={control}
										render={({ field }) => (
											<select
												{...field}
												className="select-input"
												// Add onChange handler to update state in parent
												onChange={(e) => {
													field.onChange(e);
													setFrontLean(e.target.value); // This will update the state
												}}
											>
												{leanOptions.map((o) => (
													<option key={o.value} value={o.value}>
														{o.label}
													</option>
												))}
											</select>
										)}
									/>
								</div>
								<div className="inp-col">
									<label htmlFor="frontLeanRoofPitch">
										<strong>Front Lean Roof Pitch</strong>
									</label>
									{values?.frontLeanToSide?.frontLean !== 'none' && (
										<Controller
											name="frontLeanToSide.frontLeanRoofPitch"
											control={control}
											render={({ field }) => (
												<select {...field} className="select-input">
													{leanRoofPitch.map((o) => (
														<option key={o.value} value={o.value}>
															{o.label}
														</option>
													))}
												</select>
											)}
										/>
									)}
								</div>
							</div>
							{values?.frontLeanToSide?.frontLean !== 'none' && (
								<>
									<hr className="mb-5" />
									<div className="field-section-half">
										<div className="inp-col">
											<label htmlFor="frontGableEnd">
												<strong>Front Gable End</strong>
											</label>
											{formAdditionalOptionsData?.gableEndSidingPanels ? (
												<Controller
													name="frontLeanToSide.frontGableEnd"
													control={control}
													render={({ field }) => (
														<select {...field} className="select-input">
															{gableEndsOptions.map((o) => (
																<option key={o.value} value={o.value}>
																	{o.label}
																</option>
															))}
														</select>
													)}
												/>
											) : (
												<p className="text-xs text-redColor-500 italic mt-2">
													Select Gable Orientation in Additional Options
												</p>
											)}
										</div>
										<div className="inp-col">
											<label htmlFor="leftSidewall">
												<strong>Left sidewall</strong>
											</label>
											{formAdditionalOptionsData?.sidewall ? (
												<Controller
													name="frontLeanToSide.leftSidewall"
													control={control}
													render={({ field }) => (
														<select {...field} className="select-input">
															{filterSidewallOptions(fLHeight).map((o) => (
																<option key={o.value} value={o.value}>
																	{o.label}
																</option>
															))}
														</select>
													)}
												/>
											) : (
												<p className="text-xs text-redColor-500 italic mt-2">
													Select Side Wall Orientation in Additional Options
												</p>
											)}
										</div>
									</div>
									<hr className="mb-5" />
									<div className="field-section-half">
										<div className="inp-col">
											<label htmlFor="backGableEnd">
												<strong>Back Gable End</strong>
											</label>
											{formAdditionalOptionsData?.gableEndSidingPanels ? (
												<Controller
													name="frontLeanToSide.backGableEnd"
													control={control}
													render={({ field }) => (
														<select {...field} className="select-input">
															{gableEndsOptions.map((o) => (
																<option key={o.value} value={o.value}>
																	{o.label}
																</option>
															))}
														</select>
													)}
												/>
											) : (
												<p className="text-xs text-redColor-500 italic mt-2">
													Select Gable Orientation in Additional Options
												</p>
											)}
										</div>

										<div className="inp-col">
											<label htmlFor="windBracing">
												<strong>Wind Bracing</strong>
											</label>
											<Controller
												name="frontLeanToSide.windBracing"
												control={control}
												render={({ field }) => (
													<select {...field} className="select-input">
														{windBracingOptions.map((o) => (
															<option key={o.value} value={o.value}>
																{o.label}
															</option>
														))}
													</select>
												)}
											/>
										</div>
									</div>
									<hr className="mb-5" />

									<div className="field-section-half">
										<div className="inp-col">
											<label htmlFor="jTrim">
												<strong>J-Trim</strong>
											</label>
											<Controller
												name="frontLeanToSide.jTrim"
												control={control}
												render={({ field }) => (
													<select {...field} className="select-input">
														{jTrimOptions.map((o) => (
															<option key={o.value} value={o.value}>
																{o.label}
															</option>
														))}
													</select>
												)}
											/>
										</div>
									</div>
								</>
							)}
						</>
					)}
					{activeTabSide === 'leftLeanToSide' && (
						<>
							<div className="field-section-half">
								<div className="inp-col">
									<label htmlFor="frontLean">
										<strong>Left Lean</strong>
									</label>
									<Controller
										name="leftLeanToSide.leftLean"
										control={control}
										render={({ field }) => (
											<select
												{...field}
												className="select-input"
												value={defaultOptions.leftLean} // Set the default value for leftLean
												onChange={(e) => {
													field.onChange(e); // Update the Controller's state
													const selectedValue = e.target.value;

													// Update defaultOptions and leftLean
													setDefaultOptions((prev) => ({
														...prev,
														leftLean: selectedValue, // Update leftLean in defaultOptions
													}));
													setLeftLean(selectedValue); // Update leftLean state
												}}
											>
												{leanOptions.map((o) => (
													<option key={o.value} value={o.value}>
														{o.label}
													</option>
												))}
											</select>
										)}
									/>
								</div>
								<div className="inp-col">
									<label htmlFor="frontLeanRoofPitch">
										<strong>Left Lean Roof Pitch</strong>
									</label>
									{values?.leftLeanToSide?.leftLean !== 'none' && (
										<Controller
											name="leftLeanToSide.leftLeanRoofPitch"
											control={control}
											render={({ field }) => (
												<select {...field} className="select-input">
													{leanRoofPitch.map((o) => (
														<option key={o.value} value={o.value}>
															{o.label}
														</option>
													))}
												</select>
											)}
										/>
									)}
								</div>
							</div>
							{values?.leftLeanToSide?.leftLean !== 'none' && (
								<>
									<hr className="mb-5" />
									<div className="field-section-half">
										<div className="inp-col">
											<label htmlFor="frontGableEnd">
												<strong>Front Gable End</strong>
											</label>
											{formAdditionalOptionsData?.gableEndSidingPanels ? (
												<Controller
													name="leftLeanToSide.frontGableEnd"
													control={control}
													render={({ field }) => (
														<select
															{...field}
															className="select-input"
														>
															{gableEndsOptions.map((o) => (
																<option key={o.value} value={o.value}>
																	{o.label}
																</option>
															))}
														</select>
													)}
												/>
											) : (
												<p className="text-xs text-redColor-500 italic mt-2">
													Select Gable Orientation in Additional Options
												</p>
											)}
										</div>
										<div className="inp-col">
											<label htmlFor="leftSidewall">
												<strong>Left sidewall</strong>
											</label>
											{formAdditionalOptionsData?.sidewall ? (
												<Controller
													name="leftLeanToSide.leftSidewall"
													control={control}
													render={({ field }) => (
														<select {...field} className="select-input">
															{filterSidewallOptions(lLHeight).map((o) => (
																<option key={o.value} value={o.value}>
																	{o.label}
																</option>
															))}
														</select>
													)}
												/>
											) : (
												<p className="text-xs text-redColor-500 italic mt-2">
													Select Side Wall Orientation in Additional Options
												</p>
											)}
										</div>
									</div>
									<hr className="mb-5" />
									<div className="field-section-half">
										<div className="inp-col">
											<label htmlFor="backGableEnd">
												<strong>Back Gable End</strong>
											</label>
											{formAdditionalOptionsData?.gableEndSidingPanels ? (
												<Controller
													name="leftLeanToSide.backGableEnd"
													control={control}
													render={({ field }) => (
														<select
															{...field}
															className="select-input"
														>
															{gableEndsOptions.map((o) => (
																<option key={o.value} value={o.value}>
																	{o.label}
																</option>
															))}
														</select>
													)}
												/>
											) : (
												<p className="text-xs text-redColor-500 italic mt-2">
													Select Gable Orientation in Additional Options
												</p>
											)}
										</div>

										<div className="inp-col">
											<label htmlFor="windBracing">
												<strong>Wind Bracing</strong>
											</label>
											<Controller
												name="leftLeanToSide.windBracing"
												control={control}
												render={({ field }) => (
													<select {...field} className="select-input">
														{windBracingOptions.map((o) => (
															<option key={o.value} value={o.value}>
																{o.label}
															</option>
														))}
													</select>
												)}
											/>
										</div>
									</div>
									<hr className="mb-5" />

									<div className="field-section-half">
										<div className="inp-col">
											<label htmlFor="jTrim">
												<strong>J-Trim</strong>
											</label>
											<Controller
												name="leftLeanToSide.jTrim"
												control={control}
												render={({ field }) => (
													<select {...field} className="select-input">
														{jTrimOptions.map((o) => (
															<option key={o.value} value={o.value}>
																{o.label}
															</option>
														))}
													</select>
												)}
											/>
										</div>
									</div>
								</>
							)}
						</>
					)}
					{activeTabSide === 'centerSectionSide' && (
						<>
							<div className="field-section-half">
								<div className="inp-col">
									<label htmlFor="frontGableEnd">
										<strong>Front Gable End</strong>
									</label>
									{formAdditionalOptionsData?.gableEndSidingPanels ? (
										<Controller
											name="centerSectionSide.frontGableEnd"
											control={control}
											render={({ field }) => (
														<select
															{...field}
															className="select-input"
														>
															{gableEndsOptions.map((o) => (
																<option key={o.value} value={o.value}>
																	{o.label}
																</option>
															))}
														</select>
											)}
										/>
									) : (
										<p className="text-xs text-redColor-500 italic mt-2">
											Select Gable Orientation in Additional Options
										</p>
									)}
								</div>
								<div className="inp-col">
									<label htmlFor="leftSidewall">
										<strong>Left sidewall</strong>
									</label>
									{formAdditionalOptionsData?.sidewall ? (
										<Controller
											name="centerSectionSide.leftSidewall"
											control={control}
											render={({ field }) => (
												<select {...field} className="select-input">
													{filterSidewallOptions(cSHeight).map((o) => (
														<option key={o.value} value={o.value}>
															{o.label}
														</option>
													))}
												</select>
											)}
										/>
									) : (
										<p className="text-xs text-redColor-500 italic mt-2">
											Select Side Wall Orientation in Additional Options
										</p>
									)}
								</div>
							</div>
							<hr className="mb-5" />
							<div className="field-section-half">
								<div className="inp-col">
									<label htmlFor="backGableEnd">
										<strong>Back Gable End</strong>
									</label>
									{formAdditionalOptionsData?.gableEndSidingPanels ? (
										<Controller
											name="centerSectionSide.backGableEnd"
											control={control}
											render={({ field }) => (
														<select
															{...field}
															className="select-input"
														>
															{gableEndsOptions.map((o) => (
																<option key={o.value} value={o.value}>
																	{o.label}
																</option>
															))}
														</select>
											)}
										/>
									) : (
										<p className="text-xs text-redColor-500 italic mt-2">
											Select Gable Orientation in Additional Options
										</p>
									)}
								</div>
								<div className="inp-col">
									<label htmlFor="rightSidewall">
										<strong>Right sidewall</strong>
									</label>
									{formAdditionalOptionsData?.sidewall ? (
										<Controller
											name="centerSectionSide.rightSidewall"
											control={control}
											render={({ field }) => (
												<select {...field} className="select-input">
													{sidewallOptions.map((o) => (
														<option key={o.value} value={o.value}>
															{o.label}
														</option>
													))}
												</select>
											)}
										/>
									) : (
										<p className="text-xs text-redColor-500 italic mt-2">
											Select Side Wall Orientation in Additional Options
										</p>
									)}
								</div>
							</div>
							<hr className="mb-5" />
							<div className="field-section-half">
								<div className="inp-col">
									<label htmlFor="windBracing">
										<strong>Wind Bracing</strong>
									</label>
									<Controller
										name="centerSectionSide.windBracing"
										control={control}
										render={({ field }) => (
											<select {...field} className="select-input">
												{windBracingOptions.map((o) => (
													<option key={o.value} value={o.value}>
														{o.label}
													</option>
												))}
											</select>
										)}
									/>
								</div>

								<div className="inp-col">
									<label htmlFor="jTrim">
										<strong>J-Trim</strong>
									</label>
									<Controller
										name="centerSectionSide.jTrim"
										control={control}
										render={({ field }) => (
											<select {...field} className="select-input">
												{jTrimOptions.map((o) => (
													<option key={o.value} value={o.value}>
														{o.label}
													</option>
												))}
											</select>
										)}
									/>
								</div>
							</div>
						</>
					)}
					{activeTabSide === 'backLeanToSide' && (
						<>
							<div className="field-section-half">
								<div className="inp-col">
									<label htmlFor="frontLean">
										<strong>Back Lean</strong>
									</label>
									<Controller
										name="backLeanToSide.backLean"
										control={control}
										render={({ field }) => (
											<select
												{...field}
												className="select-input"
												// Add onChange handler to update state in parent
												onChange={(e) => {
													field.onChange(e);
													setBackLean(e.target.value); // This will update the state
												}}
											>
												{leanOptions.map((o) => (
													<option key={o.value} value={o.value}>
														{o.label}
													</option>
												))}
											</select>
										)}
									/>
								</div>
								<div className="inp-col">
									<label htmlFor="frontLeanRoofPitch">
										<strong>Back Lean Roof Pitch</strong>
									</label>
									{values?.backLeanToSide?.backLean !== 'none' && (
										<Controller
											name="backLeanToSide.backLeanRoofPitch"
											control={control}
											render={({ field }) => (
												<select {...field} className="select-input">
													{leanRoofPitch.map((o) => (
														<option key={o.value} value={o.value}>
															{o.label}
														</option>
													))}
												</select>
											)}
										/>
									)}
								</div>
							</div>
							{values?.backLeanToSide?.backLean !== 'none' && (
								<>
									<hr className="mb-5" />
									<div className="field-section-half">
										<div className="inp-col">
											<label htmlFor="frontGableEnd">
												<strong>Front Gable End</strong>
											</label>
											{formAdditionalOptionsData?.gableEndSidingPanels ? (
												<Controller
													name="backLeanToSide.frontGableEnd"
													control={control}
													render={({ field }) => (
														<select
															{...field}
															className="select-input"
														>
															{gableEndsOptions.map((o) => (
																<option key={o.value} value={o.value}>
																	{o.label}
																</option>
															))}
														</select>
													)}
												/>
											) : (
												<p className="text-xs text-redColor-500 italic mt-2">
													Select Gable Orientation in Additional Options
												</p>
											)}
										</div>
										<div className="inp-col">
											<label htmlFor="leftSidewall">
												<strong>Side Wall</strong>
											</label>
											{formAdditionalOptionsData?.sidewall ? (
												<Controller
													name="backLeanToSide.rightSidewall"
													control={control}
													render={({ field }) => (
														<select {...field} className="select-input">
															{filterSidewallOptions(bLHeight).map((o) => (
																<option key={o.value} value={o.value}>
																	{o.label}
																</option>
															))}
														</select>
													)}
												/>
											) : (
												<p className="text-xs text-redColor-500 italic mt-2">
													Select Side Wall Orientation in Additional Options
												</p>
											)}
										</div>
									</div>
									<hr className="mb-5" />
									<div className="field-section-half">
										<div className="inp-col">
											<label htmlFor="backGableEnd">
												<strong>Back Gable End</strong>
											</label>
											{formAdditionalOptionsData?.gableEndSidingPanels ? (
												<Controller
													name="backLeanToSide.backGableEnd"
													control={control}
													render={({ field }) => (
														<select
															{...field}
															className="select-input"
														>
															{gableEndsOptions.map((o) => (
																<option key={o.value} value={o.value}>
																	{o.label}
																</option>
															))}
														</select>
													)}
												/>
											) : (
												<p className="text-xs text-redColor-500 italic mt-2">
													Select Gable Orientation in Additional Options
												</p>
											)}
										</div>

										<div className="inp-col">
											<label htmlFor="windBracing">
												<strong>Wind Bracing</strong>
											</label>
											<Controller
												name="backLeanToSide.windBracing"
												control={control}
												render={({ field }) => (
													<select {...field} className="select-input">
														{windBracingOptions.map((o) => (
															<option key={o.value} value={o.value}>
																{o.label}
															</option>
														))}
													</select>
												)}
											/>
										</div>
									</div>
									<hr className="mb-5" />

									<div className="field-section-half">
										<div className="inp-col">
											<label htmlFor="jTrim">
												<strong>J-Trim</strong>
											</label>
											<Controller
												name="backLeanToSide.jTrim"
												control={control}
												render={({ field }) => (
													<select {...field} className="select-input">
														{jTrimOptions.map((o) => (
															<option key={o.value} value={o.value}>
																{o.label}
															</option>
														))}
													</select>
												)}
											/>
										</div>
									</div>
								</>
							)}
						</>
					)}
					{activeTabSide === 'rightLeanToSide' && (
						<>
							<div className="field-section-half">
								<div className="inp-col">
									<label htmlFor="frontLean">
										<strong>Right Lean</strong>
									</label>
									<Controller
										name="rightLeanToSide.rightLean"
										control={control}
										render={({ field }) => (
											<select
												{...field}
												className="select-input"
												value={defaultOptions.rightLean} // Set the default value for rightLean
												onChange={(e) => {
													field.onChange(e); // Update the Controller's state
													const selectedValue = e.target.value;

													// Update defaultOptions and rightLean
													setDefaultOptions((prev) => ({
														...prev,
														rightLean: selectedValue, // Update rightLean in defaultOptions
													}));
													setRightLean(selectedValue); // Update rightLean state
												}}
											>
												{leanOptions.map((o) => (
													<option key={o.value} value={o.value}>
														{o.label}
													</option>
												))}
											</select>
										)}
									/>
								</div>
								<div className="inp-col">
									<label htmlFor="frontLeanRoofPitch">
										<strong>Right Lean Roof Pitch</strong>
									</label>
									{values?.rightLeanToSide?.rightLean !== 'none' && (
										<Controller
											name="rightLeanToSide.rightLeanRoofPitch"
											control={control}
											render={({ field }) => (
												<select {...field} className="select-input">
													{leanRoofPitch.map((o) => (
														<option key={o.value} value={o.value}>
															{o.label}
														</option>
													))}
												</select>
											)}
										/>
									)}
								</div>
							</div>
							{values?.rightLeanToSide?.rightLean !== 'none' && (
								<>
									<hr className="mb-5" />
									<div className="field-section-half">
										<div className="inp-col">
											<label htmlFor="frontGableEnd">
												<strong>Front Gable End</strong>
											</label>
											{formAdditionalOptionsData?.gableEndSidingPanels ? (
												<Controller
													name="rightLeanToSide.frontGableEnd"
													control={control}
													render={({ field }) => (
														<select {...field} className="select-input">
															{gableEndsOptions.map((o) => (
																<option key={o.value} value={o.value}>
																	{o.label}
																</option>
															))}
														</select>
													)}
												/>
											) : (
												<p className="text-xs text-redColor-500 italic mt-2">
													Select Gable Orientation in Additional Options
												</p>
											)}
										</div>
										<div className="inp-col">
											<label htmlFor="rightSidewall">
												<strong>Right sidewall</strong>
											</label>
											{formAdditionalOptionsData?.sidewall ? (
												<Controller
													name="rightLeanToSide.rightSidewall"
													control={control}
													render={({ field }) => (
														<select {...field} className="select-input">
															{filterSidewallOptions(rLHeight).map((o) => (
																<option key={o.value} value={o.value}>
																	{o.label}
																</option>
															))}
														</select>
													)}
												/>
											) : (
												<p className="text-xs text-redColor-500 italic mt-2">
													Select Side Wall Orientation in Additional Options
												</p>
											)}
										</div>
									</div>
									<hr className="mb-5" />
									<div className="field-section-half">
										<div className="inp-col">
											<label htmlFor="backGableEnd">
												<strong>Back Gable End</strong>
											</label>
											{formAdditionalOptionsData?.gableEndSidingPanels ? (
												<Controller
													name="rightLeanToSide.backGableEnd"
													control={control}
													render={({ field }) => (
														<select {...field} className="select-input">
															{gableEndsOptions.map((o) => (
																<option key={o.value} value={o.value}>
																	{o.label}
																</option>
															))}
														</select>
													)}
												/>
											) : (
												<p className="text-xs text-redColor-500 italic mt-2">
													Select Gable Orientation in Additional Options
												</p>
											)}
										</div>

										<div className="inp-col">
											<label htmlFor="windBracing">
												<strong>Wind Bracing</strong>
											</label>
											<Controller
												name="rightLeanToSide.windBracing"
												control={control}
												render={({ field }) => (
													<select {...field} className="select-input">
														{windBracingOptions.map((o) => (
															<option key={o.value} value={o.value}>
																{o.label}
															</option>
														))}
													</select>
												)}
											/>
										</div>
									</div>
									<hr className="mb-5" />

									<div className="field-section-half">
										<div className="inp-col">
											<label htmlFor="jTrim">
												<strong>J-Trim</strong>
											</label>
											<Controller
												name="rightLeanToSide.jTrim"
												control={control}
												render={({ field }) => (
													<select {...field} className="select-input">
														{jTrimOptions.map((o) => (
															<option key={o.value} value={o.value}>
																{o.label}
															</option>
														))}
													</select>
												)}
											/>
										</div>
									</div>
								</>
							)}
						</>
					)}
				</div>
			</div>
		</>
	);
};

export default SideEnds;
